import { useEffect } from "react";
import { useState } from "react";
import PostCreation from "./components/PostCreation";
import { FaceApiProvider } from "./components/ImageGeneration/FaceApi";
import { ModelLoadingScreen } from "./components/ImageGeneration/ModelLoadingScreen";
import { AccountId } from "./components/AnalysisComponent/DataUploader";
import Planner from "./components/Planner/Planner";
import { AuthProvider, useAuth } from "./components/Authentification/AuthContext";
import Login from "./components/Authentification/Login";
import { TAB_PERMISSIONS, TAB_LABELS, TabKey } from "./components/Authentification/permissions";
import Settings from "./components/Authentification/Settings";
import AdminUserManagement from "./components/Authentification/AdminUserManagement";
import AnalysisPage from "./components/AnalysisComponent/AnalysisPage";

import "./App.css"; // this being underlined red is a known error, it's only in the editor and has no consequence on runtime

type ExtendedTabKey = TabKey | "settings";

const EXTENDED_TAB_LABELS: Record<ExtendedTabKey, string> = {
  settings: "Einstellungen",
  ...TAB_LABELS,
};

function AppContent() {
  const { user, hasRole, logout } = useAuth();
  const[text, setText] = useState("");
  const[imageFile, setImageFile] = useState<File | null>(null);
  const[dataFile, setDataFile] = useState<File | null>(null);
  const[imageURL, setImageURL] = useState<string | null>(null);
  const[selectedAccount, setSelectedAccount] = useState<AccountId>("gamedays-tu-darmstadt");  

  const roleGatedTabs = (Object.keys(TAB_PERMISSIONS) as TabKey[]).filter((tab) =>
    hasRole(...TAB_PERMISSIONS[tab])
  );

  const visibleTabs: ExtendedTabKey[] = [...roleGatedTabs, "settings"];

  const [activeTab, setActiveTab] = useState<ExtendedTabKey | null>(visibleTabs[0] ?? null);

  // AppContent wird beim Login/Logout NICHT neu gemountet - nur `user` aendert sich.
  // Ohne diesen Effekt bliebe activeTab auf dem Wert des vorherigen Users stehen,
  // selbst wenn der neue User dafuer gar keine Berechtigung mehr hat.
  useEffect(() => {
    if (!activeTab || !visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0] ?? null);
    }
    // visibleTabs wird bei jedem Render neu erzeugt (Array.filter), daher
    // ueber user.username + Rollen synchronisieren statt ueber die Referenz.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username, user?.roles.join(",")]);

  // Kein User -> Login-Formular statt der App zeigen
  if (!user) {
    return <Login />;
  }

  return (
    <FaceApiProvider>
    <div>
      <header className="app-header">
          <div className="app-header__inner">
            <div className="app-brand">
                <span className="app-brand__context">
                  Arbeitsgruppe Serious Games
                </span>

                <span className="app-brand__title">
                  Content Pilot
                </span>
            </div>

            <div className="app-user">
                <div className="app-user__details">
                    <span className="app-user__name">
                      {user.username}
                    </span>

                  <span className="app-user__roles">
                    {user.roles.join(", ")}
                  </span>
                </div>

              <button
                type="button"
                className="logout-button"
                onClick={logout}
              >
                Logout
              </button>
            </div>
          </div>
      </header>

      <nav className="tab-navigation">
        <div className="tab-navigation__inner">
          {visibleTabs.map((tab) => (
            <button
              key={tab}
              className={activeTab === tab ? "tab active" : "tab"}
              onClick={() => setActiveTab(tab)}
            >
              {EXTENDED_TAB_LABELS[tab]}
            </button>
          ))}
        </div>
    </nav>

      <div className="tab-content">

        {activeTab === "settings" && <Settings />}

        {activeTab === "adminUsers" && <AdminUserManagement />}

        {activeTab === "authoring" && (
          <div >             
              <ModelLoadingScreen>
                <PostCreation text={text} setText={setText} file={imageFile} setFile={setImageFile} setImageURL={setImageURL}/>
              </ModelLoadingScreen>
          </div>
        )}

        {activeTab === "analysis" && (
          <AnalysisPage />
        )}

        {activeTab === "automation" && (
          <div>
            <Planner />
          </div>
        )}

        {visibleTabs.length === 0 && (
          <p>Für deinen Account sind aktuell keine Bereiche freigeschaltet.</p>
        )}
      </div>
    </div>
    </FaceApiProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
